import { useEffect, useState } from "react";
import MuiCheckbox from "./MuiCheckbox";
import CartContainer from "./CartContainer";
import { updateItemQuantity, removeItemFromCart, addItemToCart, CartItems, removeMultipleItemsFromCart } from "../../api/Cart";

export interface ProductFull {
    id: number;
    product_category_id: number;
    cart_item_id?: number;
    code: string;
    name: string;
    link: string;
    image: string;

    pack: string;
    price: string;
    rawPrice: number;

    qty: number;
    max: number;

    checked: boolean;
    important: boolean;

    discount?: number;
    badges?: string[];
}

export interface StoreFull {
    id: number;
    name: string;
    link: string;
    logo: string;

    voucherText: string;

    checked: boolean;
    products: ProductFull[];
}

export type Store = StoreFull;

interface CartActionProps {
    cartItems: CartItems[];
    onCartChange: () => void;
    onSelectionChange: (count: number, total: number, selectedItems: ProductFull[]) => void;
}

export default function CartAction({ cartItems, onCartChange, onSelectionChange }: CartActionProps) {
    const [stores, setStores] = useState<StoreFull[]>([]);

    useEffect(() => {
        if (cartItems) {
            // Create a lookup map for current checked status to persist it across refetches
            // Since API might not save checked status (no API call on toggle), we must preserve local state
            const checkedMap = new Map<number, boolean>();
            stores.forEach(s => {
                s.products.forEach(p => {
                    checkedMap.set(p.id, p.checked);
                });
            });

            // Group items by dummy store since API doesn't provide store info yet
            const mappedProducts: ProductFull[] = cartItems.map(item => {
                if (!item.product) {
                    console.warn(`Cart item with id ${item.id} has no product data, skipping.`);
                    return {
                        id: item.product_id,
                        product_category_id: 0,
                        cart_item_id: item.id,
                        code: "",
                        name: "Unknown Product",
                        link: "#",
                        image: "",
                        pack: "Đơn vị tính",
                        price: "0",
                        rawPrice: 0,
                        qty: Number(item.quantity),
                        max: 1000,
                        checked: false,
                        important: false,
                        discount: 0,
                        badges: []
                    };
                }
                // Sanitize price: remove dots (thousands separator in VN) and replace comma with dot (decimal) if any
                // Assuming price from API might be a formatted string "100.000" or number 100000
                let rawPrice = 0;
                if (!item.product.price) {
                    rawPrice = 0;
                }
                else if (typeof item.product.price === 'number') {
                    rawPrice = item.product.price;
                } else if (typeof item.product.price === 'string') {
                    // Remove dots, then parse
                    const str = item.product.price as string;
                    const cleanString = str.replace(/\./g, '').replace(/,/g, '.');
                    rawPrice = parseFloat(cleanString) || 0;
                }

                // Lax check for is_check from API
                let isChecked = item.is_check === 1 || String(item.is_check) === "1";

                // If we have a local state for this item, and we are just refreshing data (e.g. qty change),
                // we should trust local state over API if API doesn't persist check status.
                // However, initial load (empty stores) should respect API.
                if (checkedMap.has(item.product.id)) {
                     isChecked = checkedMap.get(item.product.id)!;
                }

                return {
                    id: item.product.id,
                    product_category_id: item.product.product_category_id,
                    cart_item_id: item.id,
                    code: item.product.slug,
                    name: item.product.name,
                    link: `/product/${item.product.slug}`,
                    image: item.product.avatar,
                    pack: "Đơn vị tính", // Default
                    price: rawPrice.toLocaleString('vi-VN'),
                    rawPrice: rawPrice,
                    qty: Number(item.quantity),
                    max: 1000,
                    checked: isChecked,
                    important: false, // Not in API
                    discount: item.product.discount,
                    badges: []
                };
            });

            if (mappedProducts.length > 0) {
                setStores([{
                    id: 1,
                    name: "Giỏ hàng của bạn",
                    link: "#",
                    logo: "",
                    voucherText: "",
                    checked: false,
                    products: mappedProducts
                }]);
            } else {
                setStores([]);
            }
        }
    }, [cartItems]);

    // Notify parent about selection changes
    useEffect(() => {
        let totalCount = 0;
        let totalPrice = 0;
        let selectedItems: ProductFull[] = [];

        stores.forEach(store => {
            store.products.forEach(product => {
                if (product.checked) {
                    totalCount += product.qty;
                    totalPrice += product.qty * product.rawPrice;
                    selectedItems.push(product);
                }
            });
        });

        // Ensure we don't trigger unnecessary updates if values haven't changed
        // But since we can't easily check prev value here without ref, we just call it.
        // Parent should handle deduping if needed, or React state update bails out if value same.
        onSelectionChange(totalCount, totalPrice, selectedItems);
    }, [stores]); // Rely on stores updates to trigger recalculation

    // tổng sản phẩm
    const allProducts = stores.flatMap(s => s.products);
    const total = allProducts.length;
    const selected = allProducts.filter(p => p.checked).length;

    const allChecked = selected === total && total > 0;
    const indeterminate = selected > 0 && selected < total;

    // toggle tất cả
    const toggleAll = () => {
        setStores(prev =>
            prev.map(store => ({
                ...store,
                checked: !allChecked,
                products: store.products.map(p => ({
                    ...p,
                    checked: !allChecked
                }))
            }))
        );
    };

    // toggle shop
    const toggleStore = (storeId: number) => {
        setStores(prev =>
            prev.map(store =>
                store.id === storeId
                    ? {
                        ...store,
                        checked: !store.checked,
                        products: store.products.map(p => ({
                            ...p,
                            checked: !store.checked
                        }))
                    }
                    : store
            )
        );
    };

    // toggle product
    const toggleProduct = (storeId: number, productId: number) => {
        setStores(prev =>
            prev.map(store =>
                store.id === storeId
                    ? {
                        ...store,
                        products: store.products.map(p =>
                            p.id === productId
                                ? { ...p, checked: !p.checked }
                                : p
                        ),
                        checked: store.products.every(p =>
                            p.id === productId ? !p.checked : p.checked
                        )
                            ? false
                            : store.checked
                    }
                    : store
            )
        );
    };

    // qty update
    const changeQty = async (storeId: number, productId: number, d: number) => {
        const store = stores.find(s => s.id === storeId);
        if (!store) return;
        const product = store.products.find(p => p.id === productId);
        if (!product || !product.cart_item_id) return;

        const newQty = product.qty + d;

        if (newQty <= 0) {
            // Remove item
            await removeItemFromCart(product.cart_item_id.toString());
            // Trigger refresh
            onCartChange();
        } else {
            // Update quantity
            await addItemToCart(product.id.toString(), d);
            // Trigger refresh
            onCartChange();
        }
    };

    // remove product
    const removeProduct = async (storeId: number, productId: number) => {
        const store = stores.find(s => s.id === storeId);
        if (!store) return;
        const product = store.products.find(p => p.id === productId);
        if (!product || !product.cart_item_id) return;

        await removeItemFromCart(product.cart_item_id.toString());
        onCartChange();
    };

    // toggle important
    const toggleImportant = (storeId: number, productId: number) => {
        setStores(prev =>
            prev.map(store =>
                store.id === storeId
                    ? {
                        ...store,
                        products: store.products.map(p =>
                            p.id === productId
                                ? { ...p, important: !p.important }
                                : p
                        )
                    }
                    : store
            )
        );
    };

    return (
        <div className="styles_cartContainer__uJI18">

            {/* Top Action bar giống hình */}
            <div className="styles_cartAction__yOdw7">
                <p className="styles_cartActionText__n8puB">
                    {selected}/{total} sản phẩm đã chọn
                </p>

                <div className="styles_cartActionRight__OVpce">
                    <div role="button" className="styles_cartRemoveAll__gUrix styles_outline__E_cox">
                        <div className="styles_cartRemoveIcon__q4_Ak">
                            <span style={{ boxSizing: 'border-box', display: 'inline-block', overflow: 'hidden', width: 'initial', height: 'initial', background: 'none', opacity: 1, border: 0, margin: 0, padding: 0, position: 'relative', maxWidth: '100%' }}>
                                <span style={{ boxSizing: 'border-box', display: 'block', width: 'initial', height: 'initial', background: 'none', opacity: 1, border: 0, margin: 0, padding: 0, maxWidth: '100%' }}>
                                    <img alt="" aria-hidden="true" src="data:image/svg+xml,%3csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20version=%271.1%27%20width=%2717%27%20height=%2717%27/%3e" style={{ display: 'block', maxWidth: '100%', width: 'initial', height: 'initial', background: 'none', opacity: 1, border: 0, margin: 0, padding: 0 }} />
                                </span>
                                <img data-origin-url="https://cdn-web-next.thuocsi.vn/images/cart/trash_green_icon.svg" src="https://cdn-web-next.thuocsi.vn/images/cart/trash_green_icon.svg" decoding="async" data-nimg="intrinsic" style={{ position: 'absolute', inset: 0, boxSizing: 'border-box', padding: 0, border: 'none', margin: 'auto', display: 'block', width: 0, height: 0, minWidth: '100%', maxWidth: '100%', minHeight: '100%', maxHeight: '100%' }} />
                            </span>
                        </div>
                        <p className="styles_cartRemoveText__beh15 hidden-1199" onClick={async () => {
                            const ids = stores.flatMap(store => store.products.filter(p => p.checked && p.cart_item_id).map(p => p.cart_item_id!.toString()));
                            if (ids.length > 0) {
                                await removeMultipleItemsFromCart(ids);
                                onCartChange();
                            }
                        }}>Xoá khỏi giỏ hàng</p>
                    </div>

                    <label
                        className="styles_cartSelectAll__3_iRm MuiFormControlLabel-labelPlacementStart"
                        onClick={toggleAll}
                    >
                        <MuiCheckbox
                            checked={allChecked}
                            indeterminate={indeterminate}
                            onChange={toggleAll}
                        />
                        <span>Chọn tất cả</span>
                    </label>

                </div>
            </div>

            {/* List store + products */}
            <CartContainer
                stores={stores}
                toggleStore={toggleStore}
                toggleProduct={toggleProduct}
                changeQty={changeQty}
                removeProduct={removeProduct}
                toggleImportant={toggleImportant}
            />
        </div>
    );
}
